import React, { ReactNode, useState } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { User } from 'firebase/auth';
import { useMutation } from '@apollo/client';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import { CircularProgress } from '@mui/material';

import { createUser, sendVerificationEmail } from '../../data/firebase';
import Copyright from '../../components/common/Copyright';
import { graphql } from '../../__generated__';

const registerAgentMutation = graphql(/* GraphQL */ `
  mutation RegisterAgent($agentToken: String!) {
    registerAgent(agentToken: $agentToken) {
      success
      message
    }
  }
`);

const Registration = () => {
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<ReactNode>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [formError, setFormError] = useState<string>();

  const location = useLocation();
  const [registerAgent] = useMutation(registerAgentMutation);

  const doCreateUser = async (password: string) => {
    if (!location.state || !location.state.email) {
      setError(
        'Unable to create user. Missing email, please go back to email verification page and try again.'
      );
    } else {
      try {
        setIsProcessing(true);
        const user: User = await createUser(location.state.email, password);
        if (user) {
          const userToken = await user.getIdTokenResult();
          const token = userToken.token;
          // register this agent pending their confirmation
          const result = await registerAgent({
            variables: {
              agentToken: token,
            },
          });
          if (result.errors) {
            console.error('Failed to register agent:', result.errors);
            setError(
              'Failed to register agent. Please try again later or contact support.'
            );
            setIsProcessing(false);
            return;
          }
          await sendVerificationEmail(
            user,
            window.location.href.replace(
              '/registration',
              '/completeRegistration'
            )
          );
          setSuccessMessage(
            <React.Fragment>
              Account successfully created. Please check your inbox for a
              verification link.{' '}
              <RouterLink to={'/login'}>
                Click here to go back to login.
              </RouterLink>
            </React.Fragment>
          );
          setError('');
          setIsProcessing(false);
        } else {
          setSuccessMessage('');
          setError('Unable to create user.');
          setIsProcessing(false);
        }
      } catch (error: any) {
        let errorMessage = `${error.message}`;
        if (errorMessage.indexOf('in-use') > 0) {
          errorMessage =
            'This email has already been registered. Please proceed to the login page.';
        } else {
          errorMessage =
            'Unable to proceed. Please contact your administrator.';
        }
        setSuccessMessage('');
        setError(errorMessage);
        setIsProcessing(false);
      }
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError('');
    const data = new FormData(event.currentTarget);
    const password = data.get('password');
    const confirmPassword = data.get('confirmPassword');

    if (typeof confirmPassword === 'string' && typeof password === 'string') {
      if (confirmPassword !== password) {
        setFormError("The passwords don't match!");
      } else {
        await doCreateUser(password);
      }
    } else {
      setFormError(
        'There was an issue with the form submission. Please contact system admin.'
      );
    }
  };

  const errorDisplay = formError || error;

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          marginBottom: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Register
        </Typography>
        {successMessage && (
          <Alert sx={{ mt: 1 }} severity="success">
            {successMessage}
          </Alert>
        )}
        {!successMessage && errorDisplay && (
          <Alert sx={{ mt: 1 }} severity="error">
            {errorDisplay}
          </Alert>
        )}
        {!successMessage && (
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ mt: 1 }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              value={location?.state?.email || ''}
              disabled
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="password"
              disabled={isProcessing}
              autoFocus
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              id="confirmPassword"
              autoComplete="confirm-password"
              disabled={isProcessing}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isProcessing}
            >
              {isProcessing ? <CircularProgress color="inherit" /> : 'Register'}
            </Button>
            <Grid container>
              <Grid item xs>
                <Link component={RouterLink} to={'/email'} variant="body2">
                  Back to email verification
                </Link>
              </Grid>
              <Grid item xs>
                <Link component={RouterLink} to={'/login'} variant="body2">
                  Back to login
                </Link>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
      <Copyright />
    </Container>
  );
};

export default Registration;
